import React from "react";
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  Center,
  Text,
  VStack,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";

const safeNumber = (val) => {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => {
  return Number.isFinite(value)
    ? value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "N/A";
};

const CryptoKPI = ({
  totalWallet: totalWalletProp = 0,
  totalWallet24h: totalWallet24hProp = 0,
  isLoading = false,
  error = null,
}) => {
  const totalWallet = safeNumber(totalWalletProp);
  const totalWallet24h = safeNumber(totalWallet24hProp);

  const hasBaseline = totalWallet24h > 0;
  const rawChange = hasBaseline
    ? ((totalWallet - totalWallet24h) / totalWallet24h) * 100
    : null;
  const dailychange =
    rawChange !== null && Number.isFinite(rawChange)
      ? Number(rawChange.toFixed(2))
      : null;

  const dollarChange = hasBaseline ? totalWallet - totalWallet24h : null;

  const getMood = () => {
    if (dailychange === null)
      return {
        emoji: "😐",
        text: "Neutral",
        color: "#718096",
        gradient: "linear-gradient(135deg, #4A5568 0%, #718096 100%)",
      };
    if (dailychange >= 10)
      return {
        emoji: "🚀",
        text: "Ecstatic!",
        color: "#9F7AEA",
        gradient: "linear-gradient(135deg, #9F7AEA 0%, #B794F4 100%)",
      };
    if (dailychange >= 0)
      return {
        emoji: "😄",
        text: "Optimistic",
        color: "#48BB78",
        gradient: "linear-gradient(135deg, #48BB78 0%, #68D391 100%)",
      };
    if (dailychange > -5)
      return {
        emoji: "😐",
        text: "Neutral",
        color: "#718096",
        gradient: "linear-gradient(135deg, #4A5568 0%, #718096 100%)",
      };
    if (dailychange > -15)
      return {
        emoji: "😓",
        text: "Pessimistic",
        color: "#F6AD55",
        gradient: "linear-gradient(135deg, #F6AD55 0%, #FBD38D 100%)",
      };
    return {
      emoji: "💀",
      text: "Panic Mode",
      color: "#FC8181",
      gradient: "linear-gradient(135deg, #FC8181 0%, #FEB2B2 100%)",
    };
  };

  const getHodlStrength = () => {
    if (dailychange === null)
      return {
        label: "Unknown",
        emoji: "❔",
        color: "#A0AEC0",
        description: "Waiting for more data",
      };

    const abs = Math.abs(dailychange);

    if (abs < 2)
      return {
        label: "Diamond Hands",
        shortLabel: "Diamond",
        emoji: "💎",
        color: "#4FD1C5",
        description: "Chill & steady, minimal volatility",
      };

    if (abs < 7)
      return {
        label: "Strong HODL",
        shortLabel: "Strong",
        emoji: "🛡️",
        color: "#48BB78",
        description: "Normal swings, still comfy",
      };

    if (abs < 15)
      return {
        label: "Shaky Hands",
        shortLabel: "Shaky",
        emoji: "😬",
        color: "#F6AD55",
        description: "Volatility rising, stay focused",
      };

    return {
      label: "Paper Hands Zone",
      shortLabel: "Paper",
      emoji: "📄",
      color: "#FC8181",
      description: "Wild moves, HODL with caution",
    };
  };

  const getDangerZone = () => {
    if (dailychange === null)
      return {
        level: "No Alert",
        emoji: "✅",
        color: "#48BB78",
        message: "Trend stable enough, no danger.",
        shortMsg: "All clear.",
      };

    if (dailychange <= -20)
      return {
        level: "CRITICAL",
        emoji: "🛑",
        color: "#E53E3E",
        message: "Heavy drop. Double-check positions.",
        shortMsg: "Heavy drop!",
      };

    if (dailychange <= -10)
      return {
        level: "HIGH",
        emoji: "⚠️",
        color: "#DD6B20",
        message: "Significant downside. Eyes on the market.",
        shortMsg: "Eyes on market.",
      };

    if (dailychange <= -5)
      return {
        level: "MEDIUM",
        emoji: "🟡",
        color: "#D69E2E",
        message: "Market in red, but not catastrophic.",
        shortMsg: "Market in red.",
      };

    return {
      level: "LOW",
      emoji: "🟢",
      color: "#38A169",
      message: "No danger. Normal or positive move.",
      shortMsg: "No danger.",
    };
  };

  const mood = getMood();
  const hodl = getHodlStrength();
  const danger = getDangerZone();

  if (isLoading) {
    return (
      <Center minH="200px">
        <VStack spacing={4}>
          <Box position="relative">
            <Spinner size="xl" color="teal.400" thickness="4px" speed="0.8s" />
            <Box
              position="absolute"
              inset={0}
              borderRadius="full"
              border="4px solid"
              borderColor="transparent"
              borderTopColor="purple.400"
              sx={{
                "@keyframes spinPulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
                animation: "spinPulse 1.5s ease-in-out infinite",
              }}
            />
          </Box>
          <Text color="gray.400" fontSize="sm" fontWeight="medium">
            Loading portfolio data...
          </Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="200px">
        <VStack spacing={3}>
          <Text fontSize="4xl">⚠️</Text>
          <Text color="red.400" fontSize="sm" fontWeight="medium">
            {error}
          </Text>
        </VStack>
      </Center>
    );
  }

  // Shared card style
  const cardBase = {
    position: "relative",
    bg: "linear-gradient(135deg, rgba(26, 32, 44, 0.98) 0%, rgba(45, 55, 72, 0.95) 100%)",
    p: { base: 3, sm: 4, md: 5 },
    borderRadius: "xl",
    borderWidth: "2px",
    textAlign: "center",
    transition: "all 0.3s ease",
    overflow: "hidden",
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };

  const isPositive = dailychange !== null && dailychange >= 0;

  return (
    <Box
      maxW={{ base: "100%", md: "900px" }}
      mx="auto"
      mt={{ base: 3, md: 6 }}
      px={{ base: 2, md: 4 }}
    >
      <SimpleGrid
        columns={{ base: 2, md: 4 }}
        spacing={{ base: 2, sm: 3, md: 5 }}
      >
        {/* TOTAL PORTFOLIO */}
        <Box
          {...cardBase}
          borderColor="rgba(56, 178, 172, 0.3)"
          _hover={{
            borderColor: "teal.400",
            transform: "translateY(-4px)",
            boxShadow: "0 20px 40px rgba(56, 178, 172, 0.3)",
          }}
        >
          <Stat>
            <StatLabel
              color="gray.400"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="bold"
              letterSpacing="wider"
              mb={{ base: 1, md: 2 }}
            >
              <Text as="span" display={{ base: "none", md: "inline" }}>
                💰 TOTAL PORTFOLIO
              </Text>
              <Text as="span" display={{ base: "inline", md: "none" }}>
                💰 PORTFOLIO
              </Text>
            </StatLabel>
            <StatNumber
              fontSize={{ base: "xl", sm: "2xl", md: "3xl" }}
              fontWeight="black"
              background="linear-gradient(135deg, #4FD1C5 0%, #38B2AC 100%)"
              backgroundClip="text"
              sx={{
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
              textShadow="0 0 30px rgba(79, 209, 197, 0.3)"
              mt={{ base: 1, md: 2 }}
              lineHeight="1.2"
            >
              ${formatNumber(totalWallet)}
            </StatNumber>
            <Text
              fontSize="xs"
              color="gray.500"
              mt={{ base: 1, md: 2 }}
              fontWeight="medium"
              display={{ base: "none", sm: "block" }}
            >
              Current value of Mattias's wallet
            </Text>
          </Stat>
        </Box>

        {/* 24H CHANGE */}
        <Box
          {...cardBase}
          borderColor={
            isPositive
              ? "rgba(72, 187, 120, 0.3)"
              : "rgba(252, 129, 129, 0.3)"
          }
          _hover={{
            borderColor: isPositive ? "green.400" : "red.400",
            transform: "translateY(-4px)",
          }}
        >
          <Stat>
            <StatLabel
              color="gray.400"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="bold"
              letterSpacing="wider"
              mb={{ base: 1, md: 2 }}
            >
              📈 24H CHANGE
            </StatLabel>
            <VStack spacing={{ base: 1, md: 2 }} mt={{ base: 1, md: 2 }}>
              {/* Icon + Percentage */}
              <HStack justify="center" spacing={{ base: 1, md: 2 }}>
                <Box
                  bg={
                    isPositive
                      ? "linear-gradient(135deg, #48BB78 0%, #68D391 100%)"
                      : "linear-gradient(135deg, #FC8181 0%, #FEB2B2 100%)"
                  }
                  borderRadius="full"
                  p={{ base: 1, md: 2 }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow={
                    isPositive
                      ? "0 4px 15px rgba(72, 187, 120, 0.4)"
                      : "0 4px 15px rgba(252, 129, 129, 0.4)"
                  }
                >
                  <Icon
                    as={isPositive ? TriangleUpIcon : TriangleDownIcon}
                    boxSize={{ base: 2.5, md: 4 }}
                    color="white"
                  />
                </Box>
                <StatNumber
                  fontSize={{ base: "xl", sm: "2xl", md: "3xl" }}
                  fontWeight="black"
                  color={isPositive ? "green.300" : "red.300"}
                  lineHeight="1.2"
                >
                  {dailychange !== null
                    ? `${dailychange >= 0 ? "+" : ""}${dailychange}%`
                    : "N/A"}
                </StatNumber>
              </HStack>

              {/* Dollar change */}
              {dollarChange !== null && (
                <HStack spacing={1} align="baseline">
                  <Text
                    fontSize={{ base: "sm", md: "xl" }}
                    fontWeight="bold"
                    color={dollarChange >= 0 ? "green.400" : "red.400"}
                  >
                    {dollarChange >= 0 ? "+" : ""}$
                    {formatNumber(Math.abs(dollarChange))}
                  </Text>
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="medium"
                    display={{ base: "none", sm: "block" }}
                  >
                    in 24h
                  </Text>
                </HStack>
              )}
            </VStack>

            <Text
              fontSize="xs"
              color="gray.500"
              mt={{ base: 1, md: 2 }}
              fontWeight="medium"
              display={{ base: "none", sm: "block" }}
            >
              {dailychange === null
                ? "Not enough data for 24h comparison"
                : dailychange >= 0
                ? "Profit in the last 24 hours"
                : "Loss in the last 24 hours"}
            </Text>
          </Stat>
        </Box>

        {/* CRYPTO MOOD */}
        <Box
          {...cardBase}
          borderColor="rgba(159, 122, 234, 0.3)"
          _hover={{
            borderColor: mood.color,
            transform: "translateY(-4px)",
            boxShadow: `0 20px 40px ${mood.color}40`,
          }}
        >
          <Stat>
            <StatLabel
              color="gray.400"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="bold"
              letterSpacing="wider"
              mb={{ base: 0, md: 2 }}
            >
              🎭 MOOD
            </StatLabel>
            <VStack spacing={{ base: 1, md: 2 }} mt={{ base: 1, md: 2 }}>
              <Box
                fontSize={{ base: "2xl", md: "4xl" }}
                sx={{
                  "@keyframes bounce": {
                    "0%, 100%": { transform: "translateY(0px)" },
                    "50%": { transform: "translateY(-8px)" },
                  },
                  animation: "bounce 3s ease-in-out infinite",
                }}
              >
                {mood.emoji}
              </Box>
              <Text
                fontSize={{ base: "md", md: "xl" }}
                fontWeight="bold"
                color={mood.color}
                textShadow={`0 0 20px ${mood.color}40`}
              >
                {mood.text}
              </Text>
            </VStack>
            <Text
              fontSize="xs"
              color="gray.500"
              mt={{ base: 1, md: 2 }}
              fontWeight="medium"
              display={{ base: "none", sm: "block" }}
            >
              Sentiment based on 24h move
            </Text>
          </Stat>
        </Box>

        {/* HODL STRENGTH / DANGER ZONE */}
        <Box
          {...cardBase}
          borderColor={`${danger.color}55`}
          _hover={{
            borderColor: danger.color,
            transform: "translateY(-4px)",
            boxShadow: `0 20px 40px ${danger.color}40`,
          }}
        >
          <Stat>
            <StatLabel
              color="gray.400"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="bold"
              letterSpacing="wider"
              mb={1}
            >
              🛡️ HODL
            </StatLabel>
            <Text
              fontSize={{ base: "sm", md: "xl" }}
              fontWeight="bold"
              color={hodl.color}
              noOfLines={1}
            >
              {hodl.emoji}{" "}
              <Text as="span" display={{ base: "none", md: "inline" }}>
                {hodl.label}
              </Text>
              <Text as="span" display={{ base: "inline", md: "none" }}>
                {hodl.shortLabel || hodl.label}
              </Text>
            </Text>

            <StatLabel
              color="gray.400"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="bold"
              letterSpacing="wider"
              mt={{ base: 2, md: 3 }}
              mb={1}
            >
              🔥 DANGER
            </StatLabel>
            <Text
              fontSize={{ base: "sm", md: "lg" }}
              fontWeight="bold"
              color={danger.color}
              noOfLines={1}
            >
              {danger.emoji} {danger.level}
            </Text>

            <Text
              fontSize="xs"
              color="gray.500"
              mt={{ base: 1, md: 2 }}
              fontWeight="medium"
              px={{ base: 0, md: 2 }}
              noOfLines={2}
              display={{ base: "none", sm: "block" }}
            >
              {danger.message}
            </Text>
            <Text
              fontSize="xs"
              color="gray.500"
              mt={1}
              fontWeight="medium"
              display={{ base: "block", sm: "none" }}
              noOfLines={1}
            >
              {danger.shortMsg}
            </Text>
          </Stat>
        </Box>
      </SimpleGrid>

      {/* FOOTER INFO */}
      <Box
        mt={{ base: 2, md: 4 }}
        p={{ base: 2, md: 3 }}
        bg="rgba(26, 32, 44, 0.6)"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="rgba(255, 221, 64, 0.2)"
        backdropFilter="blur(10px)"
      >
        <HStack
          justify="space-around"
          fontSize={{ base: "2xs", sm: "xs" }}
          color="gray.500"
          flexWrap="wrap"
          gap={{ base: 1, md: 2 }}
        >
          <HStack spacing={1}>
            <Box
              boxSize="6px"
              borderRadius="full"
              bg="green.400"
              flexShrink={0}
              sx={{
                "@keyframes pulseGreen": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
                animation: "pulseGreen 2s ease-in-out infinite",
              }}
            />
            <Text fontWeight="medium">Live Data</Text>
          </HStack>
          <Text display={{ base: "none", sm: "block" }}>•</Text>
          <Text fontWeight="medium" display={{ base: "none", sm: "block" }}>
            Updated from Mattias's wallet API
          </Text>
          <Text display={{ base: "none", sm: "block" }}>•</Text>
          <Text fontWeight="medium">Long-term HODL</Text>
        </HStack>
      </Box>
    </Box>
  );
};

export default CryptoKPI;